import { useCallback, useEffect, useRef } from 'react';

/*
  Downloads PDFs through /api/<resource>/download?fileKey= and keeps the last few as object URLs so flipping between
  cards does not re-download and object URLs are revoked when they drop out of the cache.
*/
const MAX_ENTRIES = 6;

export function usePdfBlobCache(resource) {
  const cache = useRef(new Map());
  const inflight = useRef(new Map());

  const evict = useCallback(() => {
    while (cache.current.size > MAX_ENTRIES) {
      const [oldestKey, url] = cache.current.entries().next().value;
      URL.revokeObjectURL(url);
      cache.current.delete(oldestKey);
    }
  }, []);

  const load = useCallback(
    async (key) => {
      if (!key) return null;
      const cacheKey = `${resource}:${key}`;
      if (cache.current.has(cacheKey)) {
        const url = cache.current.get(cacheKey);
        cache.current.delete(cacheKey);
        cache.current.set(cacheKey, url); // most recently used last
        return url;
      }
      if (inflight.current.has(cacheKey)) return inflight.current.get(cacheKey);
      const promise = (async () => {
        try {
          const response = await fetch(`/api/${resource}/download?fileKey=${encodeURIComponent(key)}`);
          if (!response.ok) return null;
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          cache.current.set(cacheKey, url);
          evict();
          return url;
        } catch (err) {
          return null;
        } finally {
          inflight.current.delete(cacheKey);
        }
      })();
      inflight.current.set(cacheKey, promise);
      return promise;
    },
    [resource, evict]
  );

  useEffect(() => {
    const store = cache.current;
    return () => {
      for (const url of store.values()) URL.revokeObjectURL(url);
      store.clear();
    };
  }, []);

  return { load };
}
